import type { z } from 'zod';

import type { ApiRequestArgs, EnvelopeMeta, HttpMethod, QueryParams } from './http';
import { type Paginated, zodPaginatedResponse, zodTransform } from './zodTransform';

/**
 * Turn a contract query object into the base query's `params` bag.
 *
 * List argument types across both apps are `z.input<typeof XxxQuerySchema>`,
 * and the pagination fields of those schemas are `z.coerce.number()` — whose
 * Zod 4 *input* type is `unknown`, because coercion accepts anything. That is
 * correct for the schema and useless for `QueryParams`, which carries scalars
 * only. This is the one place the narrowing happens, so no endpoint repeats
 * it.
 *
 * `undefined` and `null` are dropped rather than serialised: the API treats
 * an absent filter and an empty one very differently — an absent `contractId`
 * means "every contract in my scope", `contractId=` is a validation failure.
 */
export function toQueryParams(args: Readonly<Record<string, unknown>> | undefined): QueryParams {
  const params: Record<string, string | number | boolean> = {};

  for (const [key, value] of Object.entries(args ?? {})) {
    if (value === undefined || value === null) continue;
    params[key] =
      typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
        ? value
        : String(value);
  }

  return params;
}

/** `z.input` of a query schema when there is one, `void` when there is none. */
type DefaultArgsOf<Q extends z.ZodType | undefined> = Q extends z.ZodType ? z.input<Q> : void;

export interface EndpointConfig<Args, Q extends z.ZodType | undefined, S extends z.ZodType> {
  /** Fixed path, or built from the endpoint's own arguments (`/contracts/:id`). */
  readonly url: string | ((args: Args) => string);
  readonly method?: HttpMethod;
  /**
   * The contract query schema. Its presence is what tells `query()`/
   * `listQuery()` to read the endpoint's arguments as query-string filters
   * via {@link toQueryParams} — a route addressed by id or a mutation with a
   * body has no reason to pass this. When it *is* passed and `Args` is left
   * to its default, the endpoint's argument type becomes `z.input<Q>`
   * automatically — the one narrowing this package exists to centralise.
   */
  readonly querySchema?: Q;
  /** The contract schema for one payload — one item for `listQuery`. */
  readonly responseSchema: S;
  /** A request body built from the endpoint's own arguments. */
  readonly body?: (args: Args) => unknown;
}

function buildArgs<Args, Q extends z.ZodType | undefined, S extends z.ZodType>(
  config: EndpointConfig<Args, Q, S>,
): (args: Args) => ApiRequestArgs {
  return (args: Args) => ({
    url: typeof config.url === 'function' ? config.url(args) : config.url,
    ...(config.method === undefined ? {} : { method: config.method }),
    ...(config.querySchema === undefined
      ? {}
      : { params: toQueryParams(args as Readonly<Record<string, unknown>> | undefined) }),
    ...(config.body === undefined ? {} : { body: config.body(args) }),
  });
}

/**
 * One-liner for a non-paginated endpoint: a fixed or id-addressed detail, or
 * a mutation. Spread into `build.query`/`build.mutation` — see the package
 * README for a full, compiling example.
 *
 * ```ts
 * getContract: build.query(
 *   query({ url: (id: string) => `/contracts/${id}`, responseSchema: ContractSchema }),
 * ),
 * ```
 *
 * `Args` defaults to `z.input<typeof querySchema>` when `querySchema` is
 * given (the filtered-detail case) and to `void` otherwise (a fixed url with
 * no arguments) — both read straight off `url`/`body` when either is a
 * function, so an id-addressed route never has to repeat its id's type.
 */
export function query<
  S extends z.ZodType,
  Q extends z.ZodType | undefined = undefined,
  Args = DefaultArgsOf<Q>,
>(
  config: EndpointConfig<Args, Q, S>,
): {
  query: (args: Args) => ApiRequestArgs;
  transformResponse: (raw: unknown) => z.output<S>;
} {
  return {
    query: buildArgs(config),
    transformResponse: zodTransform(config.responseSchema),
  };
}

/**
 * One-liner for a paginated list endpoint. Same shape as {@link query}, but
 * `responseSchema` describes one *item* and the result is a {@link Paginated}
 * page built from the envelope's `meta` (`page`/`total` are never counted
 * client-side).
 *
 * ```ts
 * const listPunches = listQuery({
 *   url: '/punches',
 *   querySchema: PunchListQuerySchema,
 *   responseSchema: PunchSchema,
 * });
 * // { query, transformResponse } — spread straight into build.query:
 * getPunches: build.query(listPunches),
 * ```
 */
export function listQuery<
  S extends z.ZodType,
  Q extends z.ZodType | undefined = undefined,
  Args = DefaultArgsOf<Q>,
>(
  config: EndpointConfig<Args, Q, S>,
): {
  query: (args: Args) => ApiRequestArgs;
  transformResponse: (raw: unknown, meta: EnvelopeMeta | undefined) => Paginated<z.output<S>>;
} {
  return {
    query: buildArgs(config),
    ...zodPaginatedResponse(config.responseSchema),
  };
}
